import { useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { EntryDialog, TimesheetFilters, TimesheetGrid, useRoster } from "../components/timesheet"
import { ChevronLeft, ChevronRight, Pencil } from "../components/icons"
import { Button, ErrorNote, IconButton, PageHeader, SegmentedControl, Skeleton, useLoad } from "../components/ui"
import { type BoardConfig, type MyCompany, type TimesheetEntry, type TimesheetProject, type TimesheetResponse, api } from "../lib/api"
import { useSession } from "../lib/auth"
import {
  type TimesheetCompanies,
  type TimesheetMode,
  type TimesheetState,
  expectedMinutes,
  formatWork,
  paramsFromState,
  periodLabel,
  periodOf,
  sheetQuery,
  shiftPeriod,
  stateFromParams,
  todayKey,
} from "../lib/timesheet"

// Timesheets: the caller's own logged time as a week or a month of cards,
// with a target per day — the page that answers "have I filled in my week?".
// Modelled on YouTrack's, people selector included but for admins only: an
// admin can open a colleague's sheet (mostly an external whose hours they log)
// in the company the sidebar is on. Everyone else only ever sees their own.
//
// The URL is the whole state (?date=&mode=&projectIds=&q=&companies=&employeeId=),
// so a reload or a bookmark opens the same sheet; nothing about the page is stored.
//
// A consultant's week spans clients, so by default the sheet covers every
// company you belong to — your own entries in each — and a toggle narrows it
// to the company the sidebar is on.

type Dialog = { kind: "add"; date: string } | { kind: "edit"; entry: TimesheetEntry } | null

export function TimesheetsPage() {
  const { company, employee } = useSession()
  const [searchParams, setSearchParams] = useSearchParams()
  const state = useMemo(() => stateFromParams(searchParams), [searchParams.toString()])
  const period = periodOf(state.date, state.mode)
  const today = todayKey()

  const write = (change: Partial<TimesheetState>) => setSearchParams(paramsFromState({ ...state, ...change }))

  // Whose sheet: an admin of this company may pick anyone on its roster. The
  // server checks it again, so a pasted link gets a member nowhere.
  const isAdmin = employee.role === "admin" || employee.role === "platform_admin"
  const people = useRoster(isAdmin ? company.id : null)
  const viewing = state.employeeId && state.employeeId !== employee.id ? state.employeeId : ""
  const viewingName = viewing ? (people?.find((p) => p.id === viewing)?.name ?? "Colleague") : null
  // Everyone but you: the Person filter, offered only when there is someone.
  const colleagues = people?.filter((p) => p.id !== employee.id) ?? []
  const personOptions = isAdmin && (colleagues.length > 0 || viewing) ? colleagues : null
  // A colleague's sheet covers this company only, so the toggle has nothing
  // to choose between and the project filter follows suit.
  const sheetCompanies: TimesheetCompanies = viewing ? "current" : state.companies

  // The roster is per company, so switching companies goes back to your own.
  const lastCompany = useRef(company.id)
  useEffect(() => {
    if (lastCompany.current === company.id) return
    lastCompany.current = company.id
    if (state.employeeId) write({ employeeId: "" })
  }, [company.id])

  const query = sheetQuery(state)
  // Keyed on the company too: the switcher changes which company the request
  // acts for without changing the URL, and the sheet has to follow it.
  const sheet = useLoad(() => api.get<TimesheetResponse>(`/timesheet?${query}`), [query, company.id])
  // The same companies as the sheet, archived projects included: their
  // entries still show, so they need a name in the filter and a config for
  // the card preview.
  const projects = useLoad(
    () =>
      api
        .get<{ projects: TimesheetProject[] }>(`/timesheet/projects?companies=${sheetCompanies}`)
        .then((r) => r.projects),
    [sheetCompanies, company.id],
  )
  // Where you can log time — the add dialog's Company choices, and whether
  // there is anything for the All/current toggle to choose between.
  const memberships = useLoad(() => api.get<{ companies: MyCompany[] }>("/auth/me/companies").then((r) => r.companies))
  const companies = memberships.data ?? []
  const multi = companies.length > 1
  const showCompany = multi && sheetCompanies === "all"
  const configs = useMemo(
    () => new Map<string, BoardConfig>((projects.data ?? []).map((p) => [p.id, p.config])),
    [projects.data],
  )

  const [dialog, setDialog] = useState<Dialog>(null)
  const [filtersOpen, setFiltersOpen] = useState(false)

  const entries = sheet.data?.entries ?? []
  // Only the period's own days count: a month grid's padding shows the
  // neighbouring months' cards but does not add them to this month's total.
  const spent = entries.filter((e) => period.inPeriod(e.date)).reduce((sum, e) => sum + e.minutes, 0)
  const expected = expectedMinutes(period)

  const projectNames = (projects.data ?? [])
    .filter((p) => state.projectIds.includes(p.id))
    .map((p) => p.keyPrefix ?? p.name)

  return (
    <>
      <PageHeader
        title="Timesheets"
        subtitle={
          viewingName
            ? `${viewingName}'s logged time in ${company.name}, day by day, against an 8h working day.`
            : "Your own logged time, day by day, against an 8h working day."
        }
      />

      <div className="flex items-center gap-1 text-sm text-muted">
        <span>
          {personOptions && (
            <>
              Person: <span className="text-foreground">{viewingName ?? "Me"}</span>
              <span className="px-2 text-faint">·</span>
            </>
          )}
          Tasks: <span className="text-foreground">{state.q ? `“${state.q}”` : "All"}</span>
          <span className="px-2 text-faint">·</span>
          Projects:{" "}
          <span className="text-foreground">
            {state.projectIds.length ? projectNames.join(", ") || `${state.projectIds.length} selected` : "All"}
          </span>
        </span>
        <IconButton label="Edit filters" onClick={() => setFiltersOpen(true)}>
          <Pencil className="size-3.5" />
        </IconButton>
      </div>

      <div className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold tracking-tight text-foreground">{periodLabel(period)}</h2>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted">
            Spent time{" "}
            <span className="font-mono text-foreground tabular-nums">{formatWork(spent)}</span> of{" "}
            <span className="font-mono tabular-nums">{formatWork(expected)}</span>
          </span>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <IconButton
              label={state.mode === "week" ? "Previous week" : "Previous month"}
              onClick={() => write({ date: shiftPeriod(state.date, state.mode, -1) })}
            >
              <ChevronLeft className="size-4" />
            </IconButton>
            <Button variant="ghost" size="sm" onClick={() => write({ date: today })}>
              Today
            </Button>
            <IconButton
              label={state.mode === "week" ? "Next week" : "Next month"}
              onClick={() => write({ date: shiftPeriod(state.date, state.mode, 1) })}
            >
              <ChevronRight className="size-4" />
            </IconButton>
            {multi && !viewing && (
              <SegmentedControl<TimesheetCompanies>
                ariaLabel="Companies"
                value={state.companies}
                onChange={(companies) => write({ companies })}
                options={[
                  { value: "all", label: "All companies" },
                  { value: "current", label: company.name },
                ]}
              />
            )}
            <SegmentedControl<TimesheetMode>
              ariaLabel="Period"
              value={state.mode}
              // Switching keeps the date, as YouTrack's does: the month shown
              // is the one the week was in.
              onChange={(mode) => write({ mode })}
              options={[
                { value: "week", label: "Week" },
                { value: "month", label: "Month" },
              ]}
            />
            <Button size="sm" onClick={() => setDialog({ kind: "add", date: today })}>
              Add spent time
            </Button>
          </div>
        </div>

        <ErrorNote error={sheet.error} />
        {sheet.loading && !sheet.data ? (
          <div className="grid grid-cols-7 gap-px">
            {period.days.map((day) => (
              <Skeleton key={day} className={state.mode === "week" ? "h-80" : "h-32"} />
            ))}
          </div>
        ) : (
          !sheet.error && (
            <div className={sheet.loading ? "opacity-60 transition-opacity" : ""}>
              <TimesheetGrid
                period={period}
                entries={entries}
                today={today}
                configs={configs}
                showCompany={showCompany}
                onAdd={(date) => setDialog({ kind: "add", date })}
                onEdit={(entry) => setDialog({ kind: "edit", entry })}
              />
            </div>
          )
        )}
      </div>

      {dialog && (
        <EntryDialog
          // A fresh form per opening, so a cancelled draft never leaks into
          // the next day's add.
          key={dialog.kind === "edit" ? dialog.entry.id : `add-${dialog.date}`}
          entry={dialog.kind === "edit" ? dialog.entry : undefined}
          date={dialog.kind === "edit" ? dialog.entry.date : dialog.date}
          companies={companies}
          defaultCompanyId={company.id}
          defaultForId={dialog.kind === "add" ? viewing : undefined}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null)
            sheet.reload()
          }}
        />
      )}

      {filtersOpen && (
        <TimesheetFilters
          projects={projects.data ?? []}
          showCompany={showCompany}
          projectIds={state.projectIds}
          q={state.q}
          people={personOptions}
          employeeId={viewing}
          viewingName={viewingName}
          onClose={() => setFiltersOpen(false)}
          onApply={(filters) => {
            write(filters)
            setFiltersOpen(false)
          }}
        />
      )}
    </>
  )
}
